import type { ReactNode, SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

function Svg({
  children,
  ...props
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

/* Streamline free set (CC BY 4.0 — streamlinehq.com) — 14px grid,
   stroke 1, the same visual weight as the hand-drawn 24px/1.7 set. */
function Sl({ children, ...props }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export const PlusIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M7 .5v13M.5 6.96h13" />
  </Sl>
);

export const SearchIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M6 11.5a5.5 5.5 0 1 0 0-11a5.5 5.5 0 0 0 0 11m7.5 2L10 10" />
  </Sl>
);

export const ComposeIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M5.5 3a2.5 2.5 0 1 0-5 0v7.5l2.5 3l2.5-3zm-5 1.5h5m2-2a2 2 0 1 1 4 0v8a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1zm2 9v2" />
    <path d="M7.5 4.5h5a1 1 0 0 1 1 1v4" />
  </Sl>
);

export const SendIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="m5.812 11l2.178 2.17a1.1 1.1 0 0 0 1.05.3a1.12 1.12 0 0 0 .809-.74l3.576-10.72A1.118 1.118 0 0 0 11.987.57L1.267 4.147a1.12 1.12 0 0 0-.74.859a1.1 1.1 0 0 0 .3 1l2.737 2.737l-.09 3.466zM13.106.79L3.564 8.742" />
  </Sl>
);

export const SmileIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M7 13.5a6.5 6.5 0 1 0 0-13a6.5 6.5 0 0 0 0 13" />
    <path d="M3.7 8c.5 1.8 2.5 2.9 4.3 2.4c1.1-.4 2-1.3 2.3-2.4M4.75 5.5a.25.25 0 0 1 0-.5m0 .5a.25.25 0 0 0 0-.5m4.5.5a.25.25 0 0 1 0-.5m0 .5a.25.25 0 0 0 0-.5" />
  </Sl>
);

export const ClipIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="m12.858 6.787l-5.88 5.929a2.45 2.45 0 0 1-3.498 0l-1.759-1.789a2.5 2.5 0 0 1 0-3.508l6.265-6.285a1.976 1.976 0 0 1 2.807 0l.701.702a1.975 1.975 0 0 1 0 2.806l-5.553 5.574a.986.986 0 0 1-1.403 0l-.346-.356a.99.99 0 0 1 0-1.403L7.917 4.76" />
  </Sl>
);

export const PhoneIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6.6 3.6h3l1.5 4-2 1.4a12.2 12.2 0 0 0 5.5 5.5l1.4-2 4 1.5v3a2 2 0 0 1-2.2 2A17.6 17.6 0 0 1 4.6 5.8a2 2 0 0 1 2-2.2Z" />
  </Svg>
);

export const VideoIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M3.5 6.5a3 3 0 1 0 0-6a3 3 0 0 0 0 6m7 0a2 2 0 1 0 0-4a2 2 0 0 0 0 4M10 9H4.5a1 1 0 0 0-1 1v2.5a1 1 0 0 0 1 1H10a1 1 0 0 0 1-1V10a1 1 0 0 0-1-1m3.5 1v2.5" />
  </Sl>
);

/* tabler camera — 24px grid */
export const CameraIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 7h1a2 2 0 0 0 2-2a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2" />
    <circle cx="12" cy="13" r="3" />
  </Svg>
);

export const KeyboardIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2 6m0 2a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z" />
    <path d="M6 10l0 .01" />
    <path d="M10 10l0 .01" />
    <path d="M14 10l0 .01" />
    <path d="M18 10l0 .01" />
    <path d="M6 14l0 .01" />
    <path d="M18 14l0 .01" />
    <path d="M9 14l6 0" />
  </Svg>
);

export const UsersIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M5 6.5a2.5 2.5 0 1 0 0-5a2.5 2.5 0 0 0 0 5m-4.5 7h9v-.542A4.51 4.51 0 0 0 4.796 8.5A4.51 4.51 0 0 0 .5 12.958zm8.5-7a2.5 2.5 0 0 0 0-5m2.5 12h2v-.542A4.51 4.51 0 0 0 10 8.61" />
  </Sl>
);

/* tabler user-plus / user-check — 24px grid */
export const UserPlusIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
    <path d="M6 21v-2a4 4 0 0 1 4 -4h4" />
    <path d="M16 19h6" />
    <path d="M19 16v6" />
  </Svg>
);

export const UserCheckIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
    <path d="M6 21v-2a4 4 0 0 1 4 -4h4" />
    <path d="M15 19l2 2l4 -4" />
  </Svg>
);

export const CheckIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 12l5 5l10 -10" />
  </Svg>
);

export const ShareIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M10.5 6a2.5 2.5 0 1 0 0-5a2.5 2.5 0 0 0 0 5M3.5 9.5a2.5 2.5 0 1 0 0-5a2.5 2.5 0 0 0 0 5M10.5 13a2.5 2.5 0 1 0 0-5a2.5 2.5 0 0 0 0 5M5.7 5.7l3.1-1.6M5.7 8.3l3.1 1.6" />
  </Sl>
);

export const DotsIcon = (p: IconProps) => (
  <Svg {...p} strokeWidth={0}>
    <circle cx="5.5" cy="12" r="1.55" fill="currentColor" />
    <circle cx="12" cy="12" r="1.55" fill="currentColor" />
    <circle cx="18.5" cy="12" r="1.55" fill="currentColor" />
  </Svg>
);

export const PinIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9.2 3.6h5.6l-.8 5.3 3 3.3H7l3-3.3-.8-5.3Z" />
    <path d="M12 12.2v8.2" />
  </Svg>
);

/* `crossed` draws the muted/deafened slash through the glyph */
type CrossedProps = IconProps & { crossed?: boolean };

export const MicIcon = ({ crossed, ...p }: CrossedProps) => (
  <Svg {...p}>
    <rect x="9.2" y="3.2" width="5.6" height="10.6" rx="2.8" />
    <path d="M5.8 11.6a6.2 6.2 0 0 0 12.4 0" />
    <path d="M12 17.8V21" />
    {crossed ? <path d="M4.5 20.5 19.5 3.5" strokeWidth={2.2} /> : null}
  </Svg>
);

export const HeadphonesIcon = ({ crossed, ...p }: CrossedProps) => (
  <Svg {...p}>
    <path d="M4.4 14.4v-2.2a7.6 7.6 0 0 1 15.2 0v2.2" />
    <rect x="3.2" y="13.6" width="4.2" height="6.6" rx="1.8" />
    <rect x="16.6" y="13.6" width="4.2" height="6.6" rx="1.8" />
    {crossed ? <path d="M4.5 20.5 19.5 3.5" strokeWidth={2.2} /> : null}
  </Svg>
);

export const GearIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="m5.23 2.25l.43-1.11A1 1 0 0 1 6.59.5h.82a1 1 0 0 1 .93.64l.43 1.11l1.46.84l1.18-.18a1 1 0 0 1 1 .49l.4.7a1 1 0 0 1-.08 1.13l-.73.93v1.68l.75.93a1 1 0 0 1 .08 1.13l-.4.7a1 1 0 0 1-1 .49l-1.18-.18l-1.46.84l-.43 1.11a1 1 0 0 1-.93.64h-.84a1 1 0 0 1-.93-.64l-.43-1.11l-1.46-.84l-1.18.18a1 1 0 0 1-1-.49l-.4-.7a1 1 0 0 1 .08 1.13L2 7.84V6.16l-.75-.93a1 1 0 0 1-.08-1.13l.4-.7a1 1 0 0 1 1-.49l1.18.18zM5 7a2 2 0 1 0 4 0a2 2 0 0 0-4 0" />
  </Sl>
);

export const MessageIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M4.145 12.84a6.5 6.5 0 1 0-2.556-2.238m2.556 2.239L.5 13.5l1.089-2.897m2.556 2.238l.005-.001m-2.561-2.237l.001-.003" />
  </Sl>
);

export const XIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="m13.5.5l-13 13m0-13l13 13" />
  </Sl>
);

export const FileIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 3.5H7.4A1.9 1.9 0 0 0 5.5 5.4v13.2a1.9 1.9 0 0 0 1.9 1.9h9.2a1.9 1.9 0 0 0 1.9-1.9V8.1L14 3.5Z" />
    <path d="M13.8 3.7v4.4h4.4" />
  </Svg>
);

export const HashIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M.5 4.25h13m-13 5.5h13M11.25.5l-2.5 13m-3-13l-2.5 13" />
  </Sl>
);

export const MenuIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Svg>
);

export const SidebarIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2.6" />
    <path d="M14.5 4.5v15" />
    <path d="M17.2 10.5v3" />
  </Svg>
);

export const LinkIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="m6 3l1.464-1.464a3.536 3.536 0 0 1 5 5L11 8m-3 3l-1.464 1.465a3.536 3.536 0 0 1-5-5L3 6m6-1L5 9" />
  </Sl>
);

export const InfoIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M7 13.5a6.5 6.5 0 1 0 0-13a6.5 6.5 0 0 0 0 13M5.5 10h3" />
    <path d="M7 10V6.5H6m1-2.25a.25.25 0 0 1 0-.5m0 .5a.25.25 0 0 0 0-.5" />
  </Sl>
);

export const ChevronDownIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 9.5 6 6 6-6" />
  </Svg>
);

export const BellIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M6 13.25h2m3-7.5a4 4 0 1 0-8 0v3.5a1.5 1.5 0 0 1-1.5 1.5h11a1.5 1.5 0 0 1-1.5-1.5zM.5 5.62A6 6 0 0 1 3 .75m10.5 4.87A6 6 0 0 0 11 .75" />
  </Sl>
);

export const ClockIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M7 13.5a6.5 6.5 0 1 0 0-13a6.5 6.5 0 0 0 0 13" />
    <path d="M7 4.5V7l2.54 2.96" />
  </Sl>
);

export const StickerIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5H6.9a2.9 2.9 0 0 0-2.9 2.9v10.2a2.9 2.9 0 0 0 2.9 2.9h4.6l8.5-8.5V6.4a2.9 2.9 0 0 0-2.9-2.9Z" />
    <path d="M20 11.5h-5.6a2.9 2.9 0 0 0-2.9 2.9v5.1" />
    <path d="M9 9.4h.01M13 9.4h.01" />
  </Svg>
);

export const EmbedIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M4 10.5L.5 7L4 3.5m6 7L13.5 7L10 3.5" />
  </Sl>
);

export const GifIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M13.438 4.656v4.289s0 2-2 2H2.563s-2 0-2-2V4.656s0-2 2-2h8.875s2 0 2 2M6 5.045h1.756m0 3.51H6m.878-3.51v3.51" />
    <path d="M3.758 7.093h.878v.585a.88.88 0 0 1-.878.878h-.585a.88.88 0 0 1-.878-.878V5.923c0-.485.393-.878.878-.878h.585a.88.88 0 0 1 .828.585m4.799 2.926v-3.51h2.34m-2.34 1.608h2.047" />
  </Sl>
);

/* Status badges — filled 16px glyphs with punched-out cutouts, drawn for
   the Pfp corner badge rather than stroked like the rest of the set. */
function Badge({
  children,
  ...props
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="currentColor"
      stroke="none"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

/* Status dots + activity glyphs — real Tabler icon paths (24 viewBox).
   Silhouette paths (zalando mark, moon, briefcase body) render filled so
   they stay readable inside a ~10px dot; the rest stroke at a heavier
   weight for the same reason. */
type Glyph = { d: string; fill?: boolean };

function Tabler({
  paths,
  sw = 2,
  halo,
  ...props
}: IconProps & {
  paths: Glyph[];
  sw?: number;
  /* opaque underlay — every path gets a thick ink-colored stroke beneath
     it so the glyph reads solid over a pfp, zero see-through */
  halo?: boolean;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {halo
        ? paths.map((g) => (
            <path
              key={`h${g.d}`}
              d={g.d}
              fill={g.fill ? "var(--color-ink)" : "none"}
              stroke="var(--color-ink)"
              strokeWidth={sw + 3}
            />
          ))
        : null}
      {paths.map((g) =>
        g.fill ? (
          <path key={g.d} d={g.d} fill="currentColor" stroke="none" />
        ) : (
          <path key={g.d} d={g.d} />
        ),
      )}
    </svg>
  );
}

const ZALANDO: Glyph[] = [
  {
    d: "M7.531 21c-.65 0 -1 -.15 -1.196 -.27c-.266 -.157 -.753 -.563 -1.197 -1.747a20.583 20.583 0 0 1 -1.137 -6.983c.015 -2.745 .436 -5.07 1.137 -6.975c.444 -1.2 .93 -1.605 1.197 -1.763c.192 -.103 .545 -.262 1.195 -.262c.244 0 .532 .022 .871 .075a19.093 19.093 0 0 1 6.425 2.475h.007a19.572 19.572 0 0 1 5.287 4.508c.783 .99 .879 1.627 .879 1.942c0 .315 -.096 .953 -.879 1.943a19.571 19.571 0 0 1 -5.287 4.5h-.007a19.041 19.041 0 0 1 -6.425 2.474a5.01 5.01 0 0 1 -.871 .083z",
    fill: true,
  },
];
const MOON: Glyph[] = [
  {
    d: "M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1 -8.313 -12.454z",
    fill: true,
  },
];
const CANCEL: Glyph[] = [
  { d: "M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" },
  { d: "M18.364 5.636l-12.728 12.728" },
];
const CIRCLE: Glyph[] = [
  { d: "M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" },
];
const BRIEFCASE: Glyph[] = [
  {
    d: "M3 9a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-9z",
    fill: true,
  },
  { d: "M8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2" },
];
/* sleeping — big Z + little z staggered down-right, drawn in the
   streamline zzz style to match the Sl set's rounded caps */
const SLEEPY: Glyph[] = [
  { d: "M4 5h10L4 14h10" },
  { d: "M15 15.5h6.5l-6.5 5h6.5" },
];

type StatusIconProps = IconProps & { halo?: boolean };

export const StatusOnlineIcon = (p: StatusIconProps) => (
  <Tabler {...p} paths={ZALANDO} />
);
export const StatusIdleIcon = (p: StatusIconProps) => (
  <Tabler {...p} paths={MOON} />
);
export const StatusDndIcon = (p: StatusIconProps) => (
  <Tabler {...p} paths={CANCEL} sw={3} />
);
export const StatusOfflineIcon = (p: StatusIconProps) => (
  <Tabler {...p} paths={CIRCLE} sw={3} />
);
export const StatusWorkIcon = (p: StatusIconProps) => (
  <Tabler {...p} paths={BRIEFCASE} sw={3} />
);
export const StatusSleepyIcon = (p: StatusIconProps) => (
  <Tabler {...p} paths={SLEEPY} sw={3} />
);

/* activity glyphs — vinyl (music), gamepad (gaming), browser (browsing),
   phone-call (calls) */
export const VinylIcon = (p: IconProps) => (
  <Tabler
    {...p}
    paths={[
      { d: "M16 3.937a9 9 0 1 0 5 8.063" },
      { d: "M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" },
      { d: "M20 4m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" },
      { d: "M20 4l-3.5 10l-2.5 2" },
    ]}
  />
);
export const GamepadIcon = (p: IconProps) => (
  <Tabler
    {...p}
    paths={[
      { d: "M12 5h3.5a5 5 0 0 1 0 10h-5.5l-4.015 4.227a2.3 2.3 0 0 1 -3.923 -2.035l1.634 -8.173a5 5 0 0 1 4.904 -4.019h3.4z" },
      { d: "M14 15l4.07 4.284a2.3 2.3 0 0 0 3.925 -2.023l-1.6 -8.232" },
      { d: "M8 9v2" },
      { d: "M7 10h2" },
      { d: "M14 10h2" },
    ]}
  />
);
export const BrowserIcon = (p: IconProps) => (
  <Tabler
    {...p}
    paths={[
      { d: "M4 8h16" },
      { d: "M4 4m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z" },
      { d: "M8 4v4" },
    ]}
  />
);
export const PhoneCallIcon = (p: IconProps) => (
  <Tabler
    {...p}
    paths={[
      { d: "M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2" },
      { d: "M15 7a2 2 0 0 1 2 2" },
      { d: "M15 3a6 6 0 0 1 6 6" },
    ]}
  />
);

/* Social/brand glyphs — custom-drawn placeholders, fill style for the
   small link chips. Swap for real brand marks when assets land. */
export const GlobeIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M7 13.5a6.5 6.5 0 1 0 0-13a6.5 6.5 0 0 0 0 13" />
    <path d="M1 9.5h1.75A1.75 1.75 0 0 0 4.5 7.75v-1.5A1.75 1.75 0 0 1 6.25 4.5A1.75 1.75 0 0 0 8 2.75V.57m5.5 6.33a3.56 3.56 0 0 0-1.62-.4H9.75a1.75 1.75 0 1 0 0 3.5A1.25 1.25 0 0 1 11 11.25v.87" />
  </Sl>
);

/* Brand marks — real single-path glyphs at 24 viewBox (fill style). */
function Brand({
  children,
  ...props
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="none"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export const DiscordIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M4.112 6.5a.5.5 0 1 0 1 0a.5.5 0 1 0-1 0m4.5 0a.5.5 0 1 0 1 0a.5.5 0 1 0-1 0" />
    <path d="M.858 9.864c0-2.401.858-5.574 1.715-6.86c0 0 .858-.43 4.289-.43s4.288.43 4.288.43c.858 1.286 1.715 4.459 1.715 6.86c-.285.43-1.286 1.373-3.001 1.716l-1.51-1.886a6.6 6.6 0 0 1-2.985 0L3.86 11.58c-1.715-.343-2.716-1.287-3.002-1.716" />
    <path d="M3.86 9.007c.261.261.81.523 1.509.687a6.6 6.6 0 0 0 2.986 0c.699-.164 1.247-.426 1.509-.687" />
  </Sl>
);

export const GithubIcon = (p: IconProps) => (
  <Badge {...p}>
    <path d="M8 1a7 7 0 0 0-2.2 13.6c.35.07.5-.15.5-.33v-1.3c-2 .44-2.5-.5-2.5-.5-.32-.83-.8-1.05-.8-1.05-.66-.45.05-.44.05-.44.73.05 1.1.75 1.1.75.65 1.1 1.7.78 2.12.6.06-.47.25-.79.45-.97-1.6-.18-3.28-.8-3.28-3.55 0-.78.28-1.42.75-1.92-.08-.19-.33-.93.07-1.94 0 0 .61-.2 2 .73a6.9 6.9 0 0 1 3.64 0c1.39-.93 2-.73 2-.73.4 1.01.15 1.75.07 1.94.47.5.75 1.14.75 1.92 0 2.76-1.68 3.37-3.28 3.55.25.22.48.65.48 1.3v1.93c0 .19.14.4.52.34A7 7 0 0 0 8 1Z" />
  </Badge>
);

export const GitlabIcon = (p: IconProps) => (
  <Badge {...p}>
    <path d="M8 15 2.7 9.6a1 1 0 0 1-.3-.7L3 6.8 4.4 3.6a.5.5 0 0 1 .95-.1L6.8 6.9h2.4l1.45-3.4a.5.5 0 0 1 .95.1L13 6.8l.6 2.1a1 1 0 0 1-.3.7L8 15Z" />
  </Badge>
);

export const SteamIcon = (p: IconProps) => (
  <Brand {...p}>
    <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
  </Brand>
);

export const FlagIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M2.5.5v13m0-13l9 4.5l-9 4.5" />
  </Sl>
);

export const CompassIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M11.04 1.605a1.099 1.099 0 0 1 1.355 1.355l-1.902 6.772a1.1 1.1 0 0 1-.76.761L2.96 12.395a1.099 1.099 0 0 1-1.355-1.355l1.902-6.772c.103-.37.392-.658.76-.761z" />
    <path d="M5.689 5.689a1.854 1.854 0 1 0 2.622 2.622a1.854 1.854 0 1 0-2.622-2.622" />
  </Sl>
);

/* tabler home — 24px grid */
export const HomeIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 12l-2 0l9 -9l9 9l-2 0" />
    <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7" />
    <path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6" />
  </Svg>
);

export const HelpIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M7 13.5a6.5 6.5 0 1 0 0-13a6.5 6.5 0 0 0 0 13" />
    <path d="M5.25 5.25a1.75 1.75 0 0 1 2.093-1.717A1.752 1.752 0 0 1 8.62 5.92A1.75 1.75 0 0 1 7.002 7v1.167M7 10.459a.25.25 0 1 1 0-.5m0 .5a.25.25 0 1 0 0-.5" />
  </Sl>
);

export const GiftIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M12.5 3h-11a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1m0 4v5.5a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1V7M7 3v10.5m3-13L7 3L4 .5" />
  </Sl>
);

export const ExternalLinkIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 4.5h5.5V10" />
    <path d="M19.5 4.5 11 13" />
    <path d="M18.5 13.5V19a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 19V7.5A1.5 1.5 0 0 1 6 6h5.5" />
  </Svg>
);

export const RefreshIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 11a8 8 0 0 0-14.4-5.4L4 7" />
    <path d="M4 3v4h4" />
    <path d="M4 13a8 8 0 0 0 14.4 5.4L20 17" />
    <path d="M20 21v-4h-4" />
  </Svg>
);

export const ShopIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M1.5 8.5V13a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V8.5M8 8.5v5M1.5 10H8M.5 4L2 .5h10L13.5 4zm4.28 0v1a2 2 0 0 1-2 2H2.5a2 2 0 0 1-2-2V4" />
    <path d="M9.25 4v1a2 2 0 0 1-2 2h-.5a2 2 0 0 1-2-2V4m8.75 0v1a2 2 0 0 1-2 2h-.25a2 2 0 0 1-2-2V4" />
  </Sl>
);

export const SnowflakeIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5v17M4.7 7.75l14.6 8.5M4.7 16.25l14.6-8.5" />
    <path d="M12 3.5 10 5.5M12 3.5 14 5.5M12 20.5 10 18.5M12 20.5l2-2" />
  </Svg>
);

/* solid crown — sits tilted on the owner's pfp corner. ink outline via
   paint-order so it punches out over any avatar, filled so it reads
   at ~12px */
export const CrownIcon = (p: IconProps) => (
  <Sl {...p}>
    <path
      fill="currentColor"
      stroke="var(--color-ink)"
      strokeWidth={2.2}
      paintOrder="stroke"
      fillRule="evenodd"
      d="M7.41 1.713a.5.5 0 0 0-.82 0L3.432 6.225L.854 3.646A.5.5 0 0 0 0 4v6.5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a.5.5 0 0 0-.854-.354l-2.578 2.579z"
      clipRule="evenodd"
    />
  </Sl>
);

export const ArrowLeftIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M19 12H5m7-7-7 7 7 7" />
  </Svg>
);

export const ChevronRightIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m9.5 6 6 6-6 6" />
  </Svg>
);

export const CopyIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="9" y="9" width="11.5" height="11.5" rx="2.5" />
    <path d="M5.5 15.5V6a2.5 2.5 0 0 1 2.5-2.5h8.5" />
  </Svg>
);

export const LogoutIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M9.5 10.5v2a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v2M6.5 7h7m-2-2l2 2l-2 2" />
  </Sl>
);

export const ShieldIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="m7.92 13.21l-.59.23a.94.94 0 0 1-.66 0l-.59-.23A8 8 0 0 1 1 5.78V3A6.36 6.36 0 0 0 7 .5c1.25 1.82 3.32 2.68 6 2.5v2.78a8 8 0 0 1-5.08 7.43" />
  </Sl>
);

export const KeyIcon = (p: IconProps) => (
  <Sl {...p}>
    <path
      fill="currentColor"
      stroke="none"
      fillRule="evenodd"
      d="M10.293 1.293a1 1 0 0 1 1.414 0l2 2a1 1 0 0 1-1.414 1.414L11 3.414L9.914 4.5l1.293 1.293a1 1 0 0 1-1.414 1.414L8.5 5.914L7.446 6.968a4 4 0 1 1-1.414-1.414l1.757-1.757l.004-.004l.004-.004zM2 9a2 2 0 1 1 4 0a2 2 0 0 1-4 0"
      clipRule="evenodd"
    />
  </Sl>
);

export const NoteIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M9 13.5H1.5a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1V9z" />
    <path d="M9 9.5v4L13.5 9h-4a.5.5 0 0 0-.5.5" />
  </Sl>
);

export const DashboardIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M13 6.5H9a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 .5-.5V7a.5.5 0 0 0-.5-.5m0-6H9a.5.5 0 0 0-.5.5v2.01a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 .5-.5V1a.5.5 0 0 0-.5-.5m-8 0H1a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 .5-.5V1A.5.5 0 0 0 5 .5m0 9.99H1a.5.5 0 0 0-.5.5V13a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 .5-.5v-2.01a.5.5 0 0 0-.5-.5" />
  </Sl>
);

/* alert glyphs for the toast system — triangle warn + circled x */
export const WarnIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M7 1.6L13.2 12.4H0.8zm0 3.8v3.2m0 2h.01" />
  </Sl>
);

/* account + status-dot badge, streamline "online-status-away-account"
   style — the generic "status" glyph for toasts/labels */
export const StatusAccountIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M6 6.4a2.4 2.4 0 1 0 0-4.8a2.4 2.4 0 0 0 0 4.8M1.5 12.7c.5-3 2.5-4.5 4.5-4.5c1 0 1.9.2 2.6.6M11.4 13.3a1.9 1.9 0 1 0 0-3.8a1.9 1.9 0 0 0 0 3.8" />
  </Sl>
);

export const ErrorIcon = (p: IconProps) => (
  <Sl {...p}>
    <path d="M7 12.5a5.5 5.5 0 1 0 0-11a5.5 5.5 0 0 0 0 11M9.2 4.8l-4.4 4.4m0-4.4l4.4 4.4" />
  </Sl>
);

/* the icey logo is a white PNG — masked with currentColor it picks up
   the row's text color (fg-mute → fg on hover) like a real glyph, then
   crossfades into the real favicon while the row is hovered */
export const IceyIcon = ({ className }: { className?: string }) => (
  <span className={`relative inline-block ${className ?? ""}`}>
    <span
      aria-hidden
      className="absolute inset-0 bg-current transition-opacity duration-200 group-hover:opacity-0"
      style={{
        maskImage: "url(/icey.png)",
        WebkitMaskImage: "url(/icey.png)",
        maskSize: "contain",
        WebkitMaskSize: "contain",
        maskRepeat: "no-repeat",
        maskPosition: "center",
      }}
    />
    {/* eslint-disable-next-line @next/next/no-img-element -- logo asset */}
    <img
      src="/favicon.webp"
      alt=""
      aria-hidden
      className="absolute inset-0 h-full w-full object-contain opacity-0 transition-opacity duration-200 group-hover:opacity-100"
    />
  </span>
);

/* message-row actions — real tabler paths, 24px grid */
export const ReplyIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 14l-4 -4l4 -4" />
    <path d="M5 10h11a4 4 0 1 1 0 8h-1" />
  </Svg>
);

export const TrashIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 7l16 0" />
    <path d="M10 11l0 6" />
    <path d="M14 11l0 6" />
    <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
    <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
  </Svg>
);

export const PencilIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 20h4l10.5 -10.5a1.5 1.5 0 0 0 -4 -4l-10.5 10.5v4" />
    <path d="M13.5 6.5l4 4" />
  </Svg>
);

/* tabler eye — spoiler toggle on staged uploads */
export const EyeIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
  </Svg>
);

/* tabler star (filled) — gif favorite affordance */
export const StarIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 17.75l-6.172 3.245l1.179 -6.873l-4.993 -4.867l6.9 -1.002l3.086 -6.253l3.086 6.253l6.9 1.002l-4.993 4.867l1.179 6.873l-6.172 -3.245" />
  </Svg>
);
